import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, Loader2, ImageOff } from "lucide-react";

// Returns inspection queue (web). Company-scoped + gated server-side by mobile.returns.inspect.
// Approve resaleable → back to available stock (once); quarantine damaged. No refunds are issued here.
export default function ReturnsInspection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [photos, setPhotos] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/returns/queue");
      setRows(r.data.returns || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load returns"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadPhotos = async (rid) => {
    try {
      const r = await api.get("/attachments", { params: { record_type: "returns", record_id: rid } });
      const list = r.data?.items || r.data || [];
      const urls = [];
      for (const a of list.slice(0, 6)) {
        try { const v = await api.get(`/attachments/${a.id}/view`); urls.push(v.data.url); } catch { /* skip */ }
      }
      setPhotos((p) => ({ ...p, [rid]: urls }));
    } catch { setPhotos((p) => ({ ...p, [rid]: [] })); }
  };

  const inspect = async (rid, disposition) => {
    setBusyId(rid);
    try {
      const r = await api.post(`/returns/${rid}/inspect`, { disposition });
      toast.success(disposition === "resaleable"
        ? `Approved · ${r.data.restock_action === "restocked" ? "returned to stock" : "restock pending live stock"}`
        : "Quarantined");
      setRows((rs) => rs.filter((x) => x.id !== rid));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Inspection failed"); }
    finally { setBusyId(""); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="returns-inspection-page">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><PackageCheck className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Returns awaiting inspection</h1>
          <p className="text-sm text-slate-500">Approve resaleable items back to stock or quarantine damaged ones. No refunds are issued here.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="returns-empty">Nothing awaiting inspection. 🎉</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`return-row-${r.id}`}>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Order</div>
                  <div className="font-semibold text-slate-900">{r.order_reference}</div>
                  <div className="mt-2 text-sm text-slate-700">{r.name} <span className="text-slate-400">· {r.sku}</span></div>
                  <div className="text-sm text-slate-500">Qty {r.qty} · {r.condition} · reason: {r.reason}</div>
                  <div className="text-xs text-slate-400 mt-1">Received {new Date(r.scanned_at).toLocaleString()} by {r.scanned_by_name}</div>
                  <button onClick={() => loadPhotos(r.id)} className="mt-2 text-xs text-indigo-600 hover:underline" data-testid={`return-photos-${r.id}`}>View photos</button>
                  {photos[r.id] && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {photos[r.id].length === 0 ? <span className="text-xs text-slate-400 flex items-center gap-1"><ImageOff className="w-3 h-3" /> none</span>
                        : photos[r.id].map((u, i) => <img key={i} src={u} alt="return" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button disabled={busyId === r.id} onClick={() => inspect(r.id, "resaleable")} data-testid={`approve-${r.id}`}
                    className="rounded-lg bg-emerald-600 hover:bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Approve resaleable</button>
                  <button disabled={busyId === r.id} onClick={() => inspect(r.id, "damaged")} data-testid={`quarantine-${r.id}`}
                    className="rounded-lg bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Quarantine damaged</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
